/**
 * Générateur — Résoudre des problèmes avec une division décimale
 * Attendu ID : 617 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 9 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey), certains déclinés en plusieurs formats UI :
 *
 *   1. `part_unite_simple`        → QCM unique + Saisie
 *      Calcul d'une part (diviseur < 10), avec conversion d'unité.
 *
 *   2. `part_unite_calc`          → QCM unique + Saisie
 *      Calcul d'une part (diviseur ≥ 10), calculatrice autorisée.
 *
 *   3. `nombre_parts`             → QCM unique + Saisie
 *      Calcul d'un nombre de parts entières (interprétation du quotient).
 *
 *   4. `billet_rendu_partage`     → QCM unique + Saisie
 *      Problème composé 'billet − rendu puis ÷' (2 étapes).
 *
 *   5. `forfait_variable`         → QCM unique + Saisie
 *      Problème composé 'forfait fixe + variable ÷ unité' (calculatrice).
 *
 *   6. `choisir_calculs`          → QCM multiple
 *      Discrimination de plusieurs écritures correctes d'un même calcul.
 *
 *   7. `relier_enonces_calculs`   → Relier
 *      Associer 4 énoncés courts à 4 expressions de calcul (+, −, ×, ÷).
 *
 *   8. `vrai_faux_resolution`     → Vrai/Faux
 *      Juger 6 résolutions proposées pour un même problème (3V/3F).
 *
 *   9. `drag_resolution`          → Texte à trous (drag)
 *      Compléter la résolution d'un problème composé à 3 étapes.
 *
 * Règles dures de la fiche cluster 9 (attendu 617) :
 *   - Diviseur ≤ 9 sans calculatrice (technique), ≥ 10 AVEC calculatrice (problèmes).
 *   - Problèmes contextualisés (français simple, réalistes pour un enfant de 11 ans).
 *   - Les distracteurs matérialisent les familles d'erreur de modélisation :
 *     oubli d'étape, mauvaise opération, ordre inversé, mauvais nombre divisé,
 *     confusion part/nombre de parts, virgule décalée, unité non convertie.
 *
 * Arithmétique : multiplication entière exacte (Number), zéro imprécision flottante.
 *
 * Variantes estimées :
 *   part_unite_simple 200+ ; part_unite_calc 350+ ; nombre_parts 80+ ;
 *   billet_rendu_partage 180+ ; forfait_variable 100+ ; choisir_calculs 60+ ;
 *   relier_enonces_calculs 15+ ; vrai_faux_resolution 80+ ; drag_resolution 50+
 *   → TOTAL ≥ 1100 variantes distinctes.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 617;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PART_SIMPLE_QCM = 'Calcul d\'une part (diviseur < 10) — QCM';
const L_PART_SIMPLE_SAI = 'Calcul d\'une part (diviseur < 10) — saisie';
const L_PART_CALC_QCM   = 'Calcul d\'une part (calculatrice autorisée) — QCM';
const L_PART_CALC_SAI   = 'Calcul d\'une part (calculatrice autorisée) — saisie';
const L_NB_PARTS_QCM    = 'Calcul d\'un nombre de parts entières — QCM';
const L_NB_PARTS_SAI    = 'Calcul d\'un nombre de parts entières — saisie';
const L_BILLET_QCM      = 'Problème composé billet − rendu puis ÷ — QCM';
const L_BILLET_SAI      = 'Problème composé billet − rendu puis ÷ — saisie';
const L_FORFAIT_QCM     = 'Problème composé forfait + variable (calculatrice) — QCM';
const L_FORFAIT_SAI     = 'Problème composé forfait + variable (calculatrice) — saisie';
const L_CHOISIR         = 'Discrimination : choisir les calculs corrects (QCM multiple)';
const L_RELIER          = 'Relier 4 énoncés à leurs calculs';
const L_VF              = 'Vrai/Faux : juger 6 résolutions d\'un même problème';
const L_DRAG            = 'Drag : compléter la résolution d\'un problème composé';
// Libellé de la carte qui réunit le QCM multiple et le Vrai/Faux (revue
// 2026-08-30) : même situation, mêmes calculs à juger, deux formats.
const L_JUGER_CALCULS   = 'Reconnaître les calculs qui résolvent le problème';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES (identiques au gén. 616)
// Multiplication entière exacte via Number (valide tant que les facteurs
// strippés restent ≪ Number.MAX_SAFE_INTEGER ~9×10¹⁵)
// ═══════════════════════════════════════════════════════════

const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

const intToDecStr = (n, nbDec) => {
    const sign = n < 0 ? '-' : '';
    let s = String(Math.abs(n));
    if (nbDec === 0) return sign + s;
    while (s.length <= nbDec) s = '0' + s;
    const intPart = s.slice(0, s.length - nbDec).replace(/^0+/, '') || '0';
    const decPart = s.slice(s.length - nbDec).replace(/0+$/, '');
    return decPart === '' ? sign + intPart : `${sign}${intPart},${decPart}`;
};

const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    return intToDecStr(ai * bi, da + db);
};

const addDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const D = Math.max(da, db);
    const ai = parseInt(String(a).replace(/[\s,]/g, '') + '0'.repeat(D - da), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, '') + '0'.repeat(D - db), 10);
    return intToDecStr(ai + bi, D);
};

const subDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const D = Math.max(da, db);
    const ai = parseInt(String(a).replace(/[\s,]/g, '') + '0'.repeat(D - da), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, '') + '0'.repeat(D - db), 10);
    return intToDecStr(ai - bi, D);
};

/**
 * Division exacte d'un décimal D par un décimal ou entier d,
 * avec au plus maxDec décimales au quotient.
 *
 * Principe (entier exact, zéro flottant) :
 *   Soient da = nb de décimales de D et db = nb de décimales de d.
 *   Notons Di = D · 10^da et di = d · 10^db les entiers scalés
 *   (Di s'obtient simplement en retirant la virgule de D, idem di pour d).
 *
 *   Alors :
 *     D / d = (Di / 10^da) / (di / 10^db)
 *           = (Di / di) · 10^(db − da)
 *
 *   Pour obtenir un résultat à maxDec décimales exactes, on multiplie le
 *   numérateur par 10^maxDec supplémentaires :
 *
 *     result · 10^maxDec = (Di · 10^(maxDec + db − da)) / di
 *
 *   D'où :
 *     shift = maxDec + db − da            (≥ 0 requis, sinon null)
 *     num   = Di · 10^shift
 *
 *   Si num est divisible par di, on renvoie num / di remis à l'échelle
 *   en chaîne française « X,YZ » ; sinon null (la division ne tombe pas juste
 *   à maxDec décimales).
 *
 *   Cette formulation gère aussi bien les diviseurs entiers (db = 0) que les
 *   diviseurs décimaux (ex : forfait 0,40 €/km), sans jamais passer par
 *   Number — donc sans la moindre imprécision flottante.
 *
 * Exemples (vérifiés empiriquement) :
 *   divDec('46,00', 20)     → '2,3'   (part_unite_calc : 46 ÷ 20 = 2,30)
 *   divDec('50',    '0,40') → '125'   (forfait : 50 ÷ 0,40 = 125 km)
 *   divDec('20',    '0,80') → '25'    (forfait : 20 ÷ 0,80 = 25 h)
 *   divDec('22',    8)      → '2,75'  (drag_resolution : 22 ÷ 8 = 2,75)
 *   divDec('45,6',  8)      → '5,7'   (quotient_calc : 45,6 ÷ 8 = 5,7)
 *   divDec('1',     3)      → null    (ne tombe pas juste à 3 décimales)
 */
const divDec = (D, d, maxDec = 3) => {
    const da = decCount(D);
    const db = decCount(d);
    const Di = parseInt(String(D).replace(/[\s,]/g, ''), 10);
    const di = parseInt(String(d).replace(/[\s,]/g, ''), 10);
    if (di <= 0) return null;
    const shift = maxDec + db - da;
    if (shift < 0) return null;
    const num = Di * Math.pow(10, shift);
    if (num % di !== 0) return null;
    return intToDecStr(num / di, maxDec);
};

const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '').replace('-', '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    if (newDecs < allDigits.length) {
        const newInt = allDigits.slice(0, allDigits.length - newDecs).replace(/^0+/, '') || '0';
        const newDec = allDigits.slice(allDigits.length - newDecs).replace(/0+$/, '');
        return newDec === '' ? newInt : `${newInt},${newDec}`;
    }
    const zeros = '0'.repeat(newDecs - allDigits.length);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    return decTrim === '' ? '0' : `0,${decTrim}`;
};

const shiftRight = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '').replace('-', '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const oldDecs = decPart.length;
    const newDecs = oldDecs - k;
    if (newDecs >= 0) {
        const cutAt = allDigits.length - newDecs;
        const newInt = (allDigits.slice(0, cutAt) || '0').replace(/^0+/, '') || '0';
        const newDec = allDigits.slice(cutAt).replace(/0+$/, '');
        return newDec === '' ? newInt : `${newInt},${newDec}`;
    }
    return (allDigits + '0'.repeat(-newDecs)).replace(/^0+/, '') || '0';
};

// Facteurs de conversion vers une base par dimension (cL pour les volumes, g pour
// les masses) : deux écritures d'une même quantité physique (0,3 L et 30 cL) doivent
// être détectées comme ÉQUIVALENTES, sinon un distracteur en unité non convertie
// reproduit exactement la clé.
const UNIT_FACTOR = { L: 100, dL: 10, cL: 1, mL: 0.1, kg: 1000, g: 1 };
const isEquivalent = (a, b) => {
    const parse = (s) => {
        const str = String(s);
        const num = parseFloat(str.replace(',', '.').replace(/[^\d.\-]/g, ''));
        const um = str.match(/([a-zA-Z]+)\s*$/);
        const factor = um && (um[1] in UNIT_FACTOR) ? UNIT_FACTOR[um[1]] : 1;
        return num * factor;
    };
    return Math.abs(parse(a) - parse(b)) < 1e-6;
};

/** Écriture canonique (revue 2026-08-22, même règle que les gens 615/616) :
 *  retirer les zéros de fin (« 3,50 » → « 3,5 », « 66,00 » → « 66 ») au lieu
 *  de padder à 2 décimales. Les prix des ÉNONCÉS restent affichés tels quels. */
const formatEur2 = (s) => {
    const str = String(s);
    if (!str.includes(',')) return str;
    let t = str.replace(/0+$/, '');
    if (t.endsWith(',')) t = t.slice(0, -1);
    return t;
};

const cleanDistracteurs = (bonne, dists, fallbackFn) => {
    const out = [];
    const seen = [bonne];
    const eq = (x) => seen.some((y) => isEquivalent(x, y));
    for (const d of dists) {
        if (!eq(d)) { out.push(d); seen.push(d); }
    }
    let guard = 0;
    while (out.length < 3 && guard < 20) {
        const cand = fallbackFn(guard);
        if (!eq(cand)) { out.push(cand); seen.push(cand); }
        guard++;
    }
    return out.slice(0, 3);
};

// ═══════════════════════════════════════════════════════════
// POOLS DE CONTEXTES
// ═══════════════════════════════════════════════════════════

const PRENOMS_F = ['Léa', 'Zoé', 'Maya', 'Chloé', 'Jade', 'Emma', 'Lila', 'Nour', 'Anna', 'Louise', 'Sarah', 'Camille'];
const PRENOMS_M = ['Noé', 'Hugo', 'Tom', 'Yanis', 'Adam', 'Raphaël', 'Lucas', 'Kévin', 'Max', 'Paul'];
const pickPrenom = () => {
    const fem = Math.random() < 0.5;
    return { nom: randomChoice(fem ? PRENOMS_F : PRENOMS_M), fem };
};

/**
 * Pool A : calcul d'une part (diviseur < 10), avec unités convertibles.
 * Chaque item : { volTotal (L ou kg), unit_in, unit_out, n, part (dans unit_out), produit, recipient_sing, recipient_plu }
 */
const POOL_PART_SIMPLE = [
    // 1,5 L partagés en 4 verres → 37,5 cL
    { vol: '1,5', unit_in: 'L', unit_out: 'cL', n: 4, part: '37,5', produit: 'jus de pomme', recipient_sing: 'verre', recipient_plu: 'verres' },
    { vol: '1,2', unit_in: 'L', unit_out: 'cL', n: 4, part: '30',   produit: 'grenadine',    recipient_sing: 'verre', recipient_plu: 'verres' },
    { vol: '2',   unit_in: 'L', unit_out: 'cL', n: 5, part: '40',   produit: 'sirop',        recipient_sing: 'verre', recipient_plu: 'verres' },
    { vol: '1,8', unit_in: 'L', unit_out: 'cL', n: 6, part: '30',   produit: 'eau',          recipient_sing: 'gobelet', recipient_plu: 'gobelets' },
    { vol: '0,9', unit_in: 'L', unit_out: 'cL', n: 3, part: '30',   produit: 'lait',         recipient_sing: 'bol', recipient_plu: 'bols' },
    { vol: '2,4', unit_in: 'L', unit_out: 'cL', n: 8, part: '30',   produit: 'jus d\'orange', recipient_sing: 'verre', recipient_plu: 'verres' },
    // 4,8 kg partagés en 6 sachets → 800 g
    { vol: '4,8', unit_in: 'kg', unit_out: 'g', n: 6, part: '800', produit: 'farine',       recipient_sing: 'sachet', recipient_plu: 'sachets' },
    { vol: '3,6', unit_in: 'kg', unit_out: 'g', n: 4, part: '900', produit: 'sucre',        recipient_sing: 'paquet', recipient_plu: 'paquets' },
    { vol: '2,4', unit_in: 'kg', unit_out: 'g', n: 3, part: '800', produit: 'riz',          recipient_sing: 'sachet', recipient_plu: 'sachets' },
    { vol: '5,4', unit_in: 'kg', unit_out: 'g', n: 9, part: '600', produit: 'céréales',     recipient_sing: 'boîte',  recipient_plu: 'boîtes' },
    { vol: '7,2', unit_in: 'kg', unit_out: 'g', n: 8, part: '900', produit: 'pâtes',        recipient_sing: 'paquet', recipient_plu: 'paquets' },
    { vol: '1,5', unit_in: 'kg', unit_out: 'g', n: 5, part: '300', produit: 'chocolat',     recipient_sing: 'tablette', recipient_plu: 'tablettes' },
];

/**
 * Pool B : calcul d'une part (diviseur ≥ 10), calculatrice.
 * Total (€) ÷ n = prix unitaire exact à 2 décimales.
 */
// genreSing = 'm' ou 'f' pour accorder « un seul » / « une seule » dans la consigne.
const POOL_PART_CALC = [
    { n: 12, total: '28,56', unit: '2,38', article: 'plants de tomates', sing: 'plant',   genreSing: 'm', verbe: 'achète' },
    { n: 15, total: '42,75', unit: '2,85', article: 'cahiers',           sing: 'cahier',  genreSing: 'm', verbe: 'achète' },
    { n: 25, total: '87,50', unit: '3,50', article: 'badges',            sing: 'badge',   genreSing: 'm', verbe: 'commande' },
    { n: 18, total: '50,40', unit: '2,80', article: 'places de cinéma',  sing: 'place',   genreSing: 'f', verbe: 'réserve' },
    { n: 22, total: '66,00', unit: '3,00', article: 'goodies',           sing: 'goodie',  genreSing: 'm', verbe: 'achète' },
    { n: 16, total: '35,20', unit: '2,20', article: 'sachets',           sing: 'sachet',  genreSing: 'm', verbe: 'achète' },
    { n: 30, total: '72,00', unit: '2,40', article: 'roses',             sing: 'rose',    genreSing: 'f', verbe: 'achète' },
    { n: 20, total: '46,00', unit: '2,30', article: 'crayons de couleur', sing: 'crayon', genreSing: 'm', verbe: 'achète' },
    { n: 14, total: '49,00', unit: '3,50', article: 'livres',            sing: 'livre',   genreSing: 'm', verbe: 'commande' },
    { n: 35, total: '87,50', unit: '2,50', article: 'badges',            sing: 'badge',   genreSing: 'm', verbe: 'achète' },
    { n: 50, total: '200,00', unit: '4,00', article: 'places',           sing: 'place',   genreSing: 'f', verbe: 'vend' },
];

/**
 * Pool C : nombre de parts (interprétation du quotient).
 * Chaque item : { vol (L ou kg), unit_in, unit_out, taille (dans unit_out), nb (entier attendu), produit }
 * Ces exemples produisent un quotient fractionnaire dont l'élève doit déduire le nombre ENTIER de parts pleines.
 */
// rawQuot = quotient fractionnaire brut que l'élève obtient avant interprétation.
// C'est précisément le distracteur « j'ai écrit le quotient tel quel sans arrondir à l'entier ».
// Pour les divisions non-terminantes, rawQuot est l'approximation à 2 décimales (troncature).
// INVARIANT : rawQuot ≠ String(nb) — sinon l'exercice perd son intention pédagogique
// (interprétation d'un quotient fractionnaire).
const POOL_NB_PARTS = [
    // 1,5 L / verres de 20 cL = 150 / 20 = 7,5 → 7 verres pleins
    { vol: '1,5', unit_in: 'L', unit_out: 'cL', taille: 20, nb: 7, rawQuot: '7,5',
      produit: 'grenadine', contenant_sing: 'verre', contenant_plu: 'verres' },
    // 2 L / verres de 15 cL = 200 / 15 ≈ 13,33 → 13 verres pleins
    { vol: '2',   unit_in: 'L', unit_out: 'cL', taille: 15, nb: 13, rawQuot: '13,33',
      produit: 'sirop',     contenant_sing: 'verre', contenant_plu: 'verres' },
    // 1,8 L / gobelets de 25 cL = 180 / 25 = 7,2 → 7 gobelets pleins
    { vol: '1,8', unit_in: 'L', unit_out: 'cL', taille: 25, nb: 7, rawQuot: '7,2',
      produit: 'jus',       contenant_sing: 'gobelet', contenant_plu: 'gobelets' },
    // 12,5 kg farine / sacs 2 kg = 6,25 → 6 sacs pleins
    { vol: '12,5', unit_in: 'kg', unit_out: 'kg', taille: 2, nb: 6, rawQuot: '6,25',
      produit: 'farine', contenant_sing: 'sac', contenant_plu: 'sacs' },
    // 10 kg riz / sachets 3 kg ≈ 3,33 → 3 sachets pleins
    { vol: '10',   unit_in: 'kg', unit_out: 'kg', taille: 3, nb: 3, rawQuot: '3,33',
      produit: 'riz', contenant_sing: 'sachet', contenant_plu: 'sachets' },
    // 8,4 kg sucre / paquets 1 kg = 8,4 → 8 paquets pleins
    { vol: '8,4',  unit_in: 'kg', unit_out: 'kg', taille: 1, nb: 8, rawQuot: '8,4',
      produit: 'sucre', contenant_sing: 'paquet', contenant_plu: 'paquets' },
    // 3,6 L jus / bouteilles de 50 cL = 360/50 = 7,2 → 7 bouteilles pleines
    { vol: '3,6',  unit_in: 'L', unit_out: 'cL', taille: 50, nb: 7, rawQuot: '7,2',
      produit: 'jus de fruit', contenant_sing: 'bouteille', contenant_plu: 'bouteilles' },
    // 4,8 L lait / biberons de 75 cL = 480/75 = 6,4 → 6 biberons pleins
    { vol: '4,8',  unit_in: 'L', unit_out: 'cL', taille: 75, nb: 6, rawQuot: '6,4',
      produit: 'lait', contenant_sing: 'biberon', contenant_plu: 'biberons' },
];

/**
 * Pool D : billet/rendu → ÷ (2 étapes, sans calculatrice, diviseur < 10).
 */
const POOL_BILLET = [
    { n: 4, billet: '10', rendu: '5,20',  unit: '1,20', article: 'pains',     sing: 'pain' },
    { n: 5, billet: '20', rendu: '3,75',  unit: '3,25', article: 'cahiers',   sing: 'cahier' },
    { n: 6, billet: '20', rendu: '5,60',  unit: '2,40', article: 'stylos',    sing: 'stylo' },
    { n: 3, billet: '20', rendu: '3,50',  unit: '5,50', article: 'livres',    sing: 'livre' },
    { n: 4, billet: '20', rendu: '3,20',  unit: '4,20', article: 'classeurs', sing: 'classeur' },
    { n: 5, billet: '20', rendu: '5,75',  unit: '2,85', article: 'yaourts',   sing: 'yaourt' },
    { n: 4, billet: '10', rendu: '2,40',  unit: '1,90', article: 'croissants', sing: 'croissant' },
    { n: 8, billet: '20', rendu: '4,40',  unit: '1,95', article: 'biscuits',  sing: 'biscuit' },
    { n: 6, billet: '10', rendu: '2,80',  unit: '1,20', article: 'gâteaux',   sing: 'gâteau' },
    { n: 3, billet: '10', rendu: '1,60',  unit: '2,80', article: 'bandes dessinées', sing: 'bande dessinée' },
];

/**
 * Forfait + variable (calculatrice, diviseur décimal non entier).
 * Total = forfait + variable × unit ; variable = n (à trouver).
 * Revue 2026-08-22 : le pool figé de 8 items (176 énoncés possibles) est
 * remplacé par un tirage dynamique dans pickForfaitCtx — le total est
 * CONSTRUIT (forfait + n × tarif), donc toujours exact, aucune division
 * à vérifier.
 */
const FORFAIT_TARIFS = ['0,40', '0,50', '0,60', '0,75', '0,80', '0,90', '1,20', '1,50'];
const FORFAIT_CONTEXTES = {
    agence_location: {
        label: 'agence de location',
        phrase: (ctx, prenom) => `<p>L'agence de location Rapidcar facture un forfait fixe de ${ctx.forfait} € plus ${ctx.unit} € par kilomètre parcouru. ${prenom} paie ${ctx.total} € au total. Combien de kilomètres a-t-${ctx.pronom} parcourus ?</p>`,
        unit: 'km',
    },
    taxi: {
        label: 'taxi',
        phrase: (ctx, prenom) => `<p>Une compagnie de taxi facture ${ctx.forfait} € de prise en charge, puis ${ctx.unit} € par kilomètre parcouru. ${prenom} paie ${ctx.total} € au total. Combien de kilomètres a-t-${ctx.pronom} parcourus ?</p>`,
        unit: 'km',
    },
    parking_horaire: {
        label: 'parking',
        phrase: (ctx, prenom) => `<p>Un parking facture ${ctx.forfait} € pour l'entrée, puis ${ctx.unit} € par minute de stationnement. ${prenom} paie ${ctx.total} € au total. Combien de minutes a-t-${ctx.pronom} stationné ?</p>`,
        unit: 'min',
    },
    location_velo: {
        label: 'location de vélo',
        phrase: (ctx, prenom) => `<p>Un loueur de vélos facture un forfait de ${ctx.forfait} € plus ${ctx.unit} € par minute. ${prenom} paie ${ctx.total} € au total. Combien de minutes a-t-${ctx.pronom} roulé ?</p>`,
        unit: 'min',
    },
};

/**
 * Pool F : problèmes pour 'choisir_calculs' avec 4 options (2 vraies, 2 fausses).
 * Chaque item : { billet, rendu, n, bonne, notations: [[texte, vraiOuFaux], ...] }
 * Le problème est toujours 'billet − rendu puis ÷ n' (format canonique du cours).
 */
const POOL_CHOISIR_CALCULS = [
    { prenom: 'Diane', fem: true,  article: 'pains',      n: 4, billet: '10', rendu: '5,20', bonne: '1,20' },
    { prenom: 'Léa',   fem: true,  article: 'cahiers',    n: 5, billet: '20', rendu: '3,75', bonne: '3,25' },
    { prenom: 'Tom',   fem: false, article: 'croissants', n: 4, billet: '10', rendu: '2,40', bonne: '1,90' },
    { prenom: 'Hugo',  fem: false, article: 'livres',     n: 3, billet: '20', rendu: '3,50', bonne: '5,50' },
    { prenom: 'Anna',  fem: true,  article: 'stylos',     n: 6, billet: '20', rendu: '5,60', bonne: '2,40' },
];

/**
 * Pool G : relier énoncés ↔ calculs.
 * Chaque pool : 4 énoncés courts (sources) qui utilisent les mêmes 2 nombres (a, b)
 * mais pour 4 opérations différentes. Force la lecture sémantique.
 */
const POOL_RELIER_ENONCES = [
    {
        a: '3', b: '7,50',
        items: [
            { enonce: "Léa a 3 € dans sa tirelire. Elle reçoit 7,50 € pour son anniversaire. Combien a-t-elle maintenant ?", calcul: '3 + 7,50' },
            { enonce: "Hugo avait 7,50 € et il a dépensé 3 € au goûter. Combien lui reste-t-il ?", calcul: '7,50 − 3' },
            { enonce: "Nour achète 3 gâteaux à 7,50 € chacun. Combien paie-t-elle en tout ?", calcul: '7,50 × 3' },
            { enonce: "Camille achète 3 stylos identiques. Elle paie 7,50 € au total. Quel est le prix d'un stylo ?", calcul: '7,50 ÷ 3' },
        ],
    },
    {
        a: '4', b: '9,60',
        items: [
            { enonce: "Maya avait 4 € et trouve un billet de 9,60 € dans un livre. Combien a-t-elle maintenant ?", calcul: '4 + 9,60' },
            { enonce: "Tom avait 9,60 € et a dépensé 4 € au marché. Combien lui reste-t-il ?", calcul: '9,60 − 4' },
            { enonce: "Chloé achète 4 BD à 9,60 € chacune. Combien paie-t-elle au total ?", calcul: '9,60 × 4' },
            { enonce: "Adam achète 4 paquets identiques. Il paie 9,60 € en tout. Quel est le prix d'un paquet ?", calcul: '9,60 ÷ 4' },
        ],
    },
    {
        a: '5', b: '12,50',
        items: [
            { enonce: "Jade a 5 € et reçoit 12,50 € supplémentaires. Combien a-t-elle maintenant ?", calcul: '5 + 12,50' },
            { enonce: "Max avait 12,50 € et a dépensé 5 € au cinéma. Combien lui reste-t-il ?", calcul: '12,50 − 5' },
            { enonce: "Louise achète 5 sacs à 12,50 € chacun. Combien paie-t-elle au total ?", calcul: '12,50 × 5' },
            { enonce: "Yanis achète 5 sandwichs identiques. Il paie 12,50 € en tout. Quel est le prix d'un sandwich ?", calcul: '12,50 ÷ 5' },
        ],
    },
    {
        a: '6', b: '14,40',
        items: [
            { enonce: "Zoé a 6 € et reçoit 14,40 € de sa grand-mère. Combien a-t-elle maintenant ?", calcul: '6 + 14,40' },
            { enonce: "Paul avait 14,40 € et a dépensé 6 € au restaurant. Combien lui reste-t-il ?", calcul: '14,40 − 6' },
            { enonce: "Emma achète 6 places à 14,40 € chacune. Combien paie-t-elle au total ?", calcul: '14,40 × 6' },
            { enonce: "Lucas achète 6 cahiers identiques. Il paie 14,40 € en tout. Quel est le prix d'un cahier ?", calcul: '14,40 ÷ 6' },
        ],
    },
];

/**
 * Pool H : problème unique pour vrai/faux resolution (billet-rendu composé).
 */
const POOL_VF_RESOLUTION = [
    { prenom: 'Tom',   fem: false, n: 4, article: 'croissants', sing: 'croissant', billet: '10', rendu: '2,40', bonne: '1,90', paye: '7,60' },
    { prenom: 'Léa',   fem: true,  n: 5, article: 'cahiers',    sing: 'cahier',    billet: '20', rendu: '3,75', bonne: '3,25', paye: '16,25' },
    { prenom: 'Diane', fem: true,  n: 4, article: 'pains',      sing: 'pain',      billet: '10', rendu: '5,20', bonne: '1,20', paye: '4,80' },
    { prenom: 'Anna',  fem: true,  n: 6, article: 'stylos',     sing: 'stylo',     billet: '20', rendu: '5,60', bonne: '2,40', paye: '14,40' },
];

/**
 * Pool I : problèmes composés en 3 étapes (× puis + puis ÷) pour drag_resolution.
 * Chaque item : { prenom, n_enfants, gateau, jus_unit, total_jus, depense, part }
 */
const POOL_DRAG_RESOLUTION = [
    // Louise : 8 enfants, gâteau 12,40 €, 8 jus à 1,20 €, total 22 €, part 2,75 €
    { prenom: 'Louise', n: 8, gateau: '12,40', jus: '1,20', total_jus: '9,60', depense: '22',    part: '2,75' },
    { prenom: 'Sarah',  n: 5, gateau: '8,50',  jus: '1,30', total_jus: '6,50', depense: '15',    part: '3' },
    { prenom: 'Emma',   n: 4, gateau: '6,40',  jus: '0,90', total_jus: '3,60', depense: '10',    part: '2,50' },
    { prenom: 'Lucas',  n: 6, gateau: '9,60',  jus: '1,40', total_jus: '8,40', depense: '18',    part: '3' },
    { prenom: 'Chloé',  n: 5, gateau: '7,50',  jus: '1,10', total_jus: '5,50', depense: '13',    part: '2,60' },
];

// ═══════════════════════════════════════════════════════════
// GÉNÉRATEURS DYNAMIQUES (Choisir / Relier / VF / Drag)
// ═══════════════════════════════════════════════════════════

/** Convertit centimes (entier) en string "X,YZ" français. */
const formatCentimes = (scaled) => {
    const intPart = Math.floor(scaled / 100);
    const cents = scaled % 100;
    if (cents === 0) return String(intPart);
    if (cents % 10 === 0) return `${intPart},${cents / 10}`;
    return `${intPart},${cents < 10 ? '0' : ''}${cents}`;
};

/** Pool d'articles (achat/vente) avec singulier/pluriel pour les énoncés. */
const ARTICLES_ACHAT = [
    { plu: 'pains',      sing: 'pain' },
    { plu: 'cahiers',    sing: 'cahier' },
    { plu: 'croissants', sing: 'croissant' },
    { plu: 'livres',     sing: 'livre' },
    { plu: 'stylos',     sing: 'stylo' },
    { plu: 'gommes',     sing: 'gomme' },
    { plu: 'classeurs',  sing: 'classeur' },
    { plu: 'pommes',     sing: 'pomme' },
    { plu: 'kiwis',      sing: 'kiwi' },
    { plu: 'crayons',    sing: 'crayon' },
    { plu: 'cartes',     sing: 'carte' },
    { plu: 'sandwichs',  sing: 'sandwich' },
];

/**
 * Génère dynamiquement un problème billet-rendu÷n.
 * Retourne { prenom, fem, article, sing, n, billet, rendu, bonne, paye }.
 * Garantit (billet−rendu) ÷ n fini avec ≤ 2 décimales.
 */
const genChoisirData = () => {
    for (let attempts = 0; attempts < 30; attempts++) {
        const n = randInt(3, 8);
        const billet = randomChoice(['5', '10', '20', '50']);
        const billetScaled = parseInt(billet) * 100;
        // rendu ∈ [20 ct ; billet - n × 50 ct], multiple de n pour (paye/n) fini
        const minRendu = 20;
        const maxRendu = billetScaled - n * 50;
        if (maxRendu < minRendu) continue;
        const renduScaled = randInt(minRendu, maxRendu);
        const payeScaled = billetScaled - renduScaled;
        if (payeScaled % n !== 0) continue;        // (paye / n) doit être entier en centimes
        const bonneScaled = payeScaled / n;
        if (bonneScaled < 50) continue;             // bonne ≥ 0,50 €
        const { nom: prenom, fem } = pickPrenom();
        const art = randomChoice(ARTICLES_ACHAT);
        return {
            prenom, fem,
            article: art.plu,
            sing: art.sing,
            n,
            billet,
            rendu: formatCentimes(renduScaled),
            bonne: formatCentimes(bonneScaled),
            paye: formatCentimes(payeScaled),
        };
    }
    return null; // échec rare → caller utilisera fallback
};

/**
 * Pool de contextes pour les 4 templates Relier (+, −, ×, ÷).
 * Chaque template prend (prenom, fem, a, b, ctx) et produit un énoncé.
 */
const RELIER_CONTEXTES_PLUS = ['pour son anniversaire', 'de sa grand-mère', 'pour Noël', 'comme étrennes', 'en cadeau'];
const RELIER_CONTEXTES_MOINS = ['au goûter', 'au cinéma', 'au marché', 'à la boulangerie', 'au restaurant'];

/**
 * Génère 4 paires (énoncé, calcul) basées sur un même couple (a, b)
 * et un même prénom, mais 4 opérations différentes.
 */
const genRelierData = () => {
    const a = randInt(3, 8);                       // entier (nombre d'objets ou tirelire)
    // b > a garanti (revue 2026-08-30) : l'énoncé de soustraction pose « avait b €,
    // a dépensé a € ». Tirés indépendamment, 8,7 % des cas donnaient « avait 5,91 €,
    // a dépensé 8 € » — situation impossible, et résultat négatif hors programme 6e.
    // Marge de 0,50 € pour que le reste ne soit jamais nul non plus.
    const bScaled = randInt(a * 100 + 50, 3000);   // décimal ∈ ]a ; 30,00]
    const b = formatCentimes(bScaled);
    const { nom: prenom, fem } = pickPrenom();
    const elle = fem ? 'elle' : 'il';
    const Elle = fem ? 'Elle' : 'Il';
    const causePlus = randomChoice(RELIER_CONTEXTES_PLUS);
    const lieuMoins = randomChoice(RELIER_CONTEXTES_MOINS);
    const art = randomChoice(ARTICLES_ACHAT);
    return {
        items: [
            {
                enonce: `${prenom} a ${a} € dans sa tirelire. ${Elle} reçoit ${b} € ${causePlus}. Combien a-t-${elle} maintenant ?`,
                calcul: `${a} + ${b}`,
            },
            {
                enonce: `${prenom} avait ${b} € et ${elle} a dépensé ${a} € ${lieuMoins}. Combien lui reste-t-il ?`,
                calcul: `${b} − ${a}`,
            },
            {
                enonce: `${prenom} achète ${a} ${art.plu} à ${b} € ${art.sing.endsWith('e') ? 'chacune' : 'chacun'}. Combien paie-t-${elle} en tout ?`,
                calcul: `${b} × ${a}`,
            },
            {
                // Deux ordres de lecture (revue 2026-08-31, particularité
                // importée du relier du gen 619 avant sa mise au placard) :
                // le diviseur apparaît tantôt AVANT, tantôt APRÈS le dividende
                // — l'élève doit lire la structure, pas la position des nombres.
                enonce: randomChoice([
                    `${prenom} achète ${a} ${art.plu} identiques. ${Elle} paie ${b} € en tout. Quel est le prix d'un ${art.sing} ?`,
                    `${prenom} paie ${b} € pour ${a} ${art.plu} identiques. Quel est le prix d'un ${art.sing} ?`,
                ]),
                calcul: `${b} ÷ ${a}`,
            },
        ],
    };
};

/** Pool de prix de jus usuels (toujours décimal fini). */
const JUS_PRIX = ['0,90', '1,10', '1,20', '1,30', '1,40', '1,50', '1,60', '1,80', '2'];

/**
 * Génère un problème "n enfants achètent gâteau + jus" pour drag_resolution.
 * Garantit que part = depense ÷ n est décimal fini ≤ 2 décimales.
 */
const genDragResData = () => {
    for (let attempts = 0; attempts < 30; attempts++) {
        const n = randInt(4, 8);
        // gateau ∈ [5,00 ; 15,00] tel que gateau × 100 multiple de n (gateau/n fini)
        let gateauScaled;
        let found = false;
        for (let g = 0; g < 30; g++) {
            const candidat = randInt(500, 1500);
            if (candidat % n === 0) { gateauScaled = candidat; found = true; break; }
        }
        if (!found) continue;
        const gateau = formatCentimes(gateauScaled);
        const jus = randomChoice(JUS_PRIX);
        const total_jus = mulDec(jus, String(n));
        const depense = addDec(gateau, total_jus);
        const part = divDec(depense, n, 3);
        // Sécurité : part doit être ≤ 2 décimales (devrait toujours être vrai)
        const partDec = part.includes(',') ? part.split(',')[1].length : 0;
        if (partDec > 2) continue;
        const { nom: prenom } = pickPrenom();
        return { prenom, n, gateau, jus, total_jus, depense, part };
    }
    return null;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — part_unite_simple (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const pickPartSimple = () => {
    const item = randomChoice(POOL_PART_SIMPLE);
    const { nom: prenom, fem } = pickPrenom();
    return { ...item, prenom, fem };
};

const buildEnoncePartSimple = (ctx) => {
    const { prenom, vol, unit_in, n, produit, recipient_plu } = ctx;
    const verbes = ['partage', 'verse', 'répartit'];
    const verbe = randomChoice(verbes);
    // Élision « de » → « d' » devant voyelle ou h muet
    const first = produit.normalize('NFD').replace(/[\u0300-\u036f]/g, '')[0].toLowerCase();
    const dePrep = /[aeiouh]/.test(first) ? "d'" : 'de ';
    // Unité de sortie imposée : sans elle, « 0,3 L » et « 30 cL » sont deux réponses
    // exactes de la même quantité → non-unicité.
    return `<p class="italic text-slate-400">✏️ Pense à poser tes calculs sur ton cahier ou au brouillon avant de répondre.</p><p>${prenom} ${verbe} ${vol} ${unit_in} ${dePrep}${produit} équitablement entre ${n} ${recipient_plu}. Combien chaque ${ctx.recipient_sing} contient-il, en ${ctx.unit_out} ?</p>`;
};

const genPartSimpleQcm = () => {
    const ctx = pickPartSimple();
    const { vol, unit_out, n, part } = ctx;   // unit_in plus utilisé : tous les choix sont en unit_out
    // Distracteurs fiche §6 exercice A (617) :
    //  - MULTIPLICATION : vol × n (en gardant unité d'entrée)
    //  - ORDRE INVERSÉ : n ÷ vol (signe le mauvais cas, aberrant numériquement)
    //  - VIRGULE DÉCALÉE : part × 10
    //  - UNITÉ NON CONVERTIE (résultat en unit_in au lieu d'unit_out)
    // Sélection de 3 parmi ces familles pour ne garder que 3 distracteurs.
    const distShiftDec = shiftRight(part, 1); // part × 10
    // Erreur-type « oubli de la conversion » : l'élève calcule vol ÷ n (valeur en
    // unit_in) mais l'écrit avec l'unité de sortie → nombre FAUX (0,3 cL ≠ 30 cL).
    // L'ancien candidat en unit_in (0,3 L) valait la clé (0,3 L = 30 cL) → 2e réponse juste.
    const distOubliConv = divDec(vol, n, 3);

    const bonne = `${part} ${unit_out}`;

    // Candidats (format unifié avec suffixe unit_out)
    const candidats = [
        `${mulDec(part, '10')} ${unit_out}`,   // virgule décalée
        `${distOubliConv} ${unit_out}`,        // oubli de conversion (nombre non converti, unité de sortie)
        `${shiftLeft(part, 1)} ${unit_out}`,   // virgule divisée ÷10
    ];

    const distracteurs = cleanDistracteurs(bonne, candidats, (i) => {
        if (i === 0) return `${mulDec(vol, String(n))} ${unit_out}`;
        if (i === 1) return `${parseInt(n, 10) * 2} ${unit_out}`;
        return `${mulDec(part, '2')} ${unit_out}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnoncePartSimple(ctx),
        opts(L_PART_SIMPLE_QCM),
    );
    payload._meta.generatorKey = 'part_unite_simple';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignaturePartSaisie617 = null;

const genPartSimpleSaisie = () => {
    let payload = construirePartSimpleSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePartSaisie617; t++) {
        payload = construirePartSimpleSaisie();
    }
    derniereSignaturePartSaisie617 = payload.texte;
    return payload;
};

const construirePartSimpleSaisie = () => {
    const ctx = pickPartSimple();
    const payload = makeSaisie(
        ctx.part,
        buildEnoncePartSimple(ctx),
        { ...opts(L_PART_SIMPLE_SAI), strict: false, suffixe: ctx.unit_out },
    );
    payload._meta.generatorKey = 'part_unite_simple';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — part_unite_calc (QCM + Saisie, sans calculatrice depuis 2026-08-28)
// ═══════════════════════════════════════════════════════════

const pickPartCalc = () => {
    const item = randomChoice(POOL_PART_CALC);
    const { nom: prenom, fem } = pickPrenom();
    // Vérification n × unit == total
    const check = mulDec(item.unit, String(item.n));
    if (!isEquivalent(check, item.total)) {
        return { n: 12, total: '28,56', unit: '2,38', article: 'plants de tomates',
                 sing: 'plant', genreSing: 'm', verbe: 'achète', prenom, fem };
    }
    return { ...item, prenom, fem };
};

const buildEnoncePartCalc = (ctx) => {
    const { prenom, n, total, article, genreSing } = ctx;
    // Accord grammatical : « un seul » / « une seule » selon le genre du singulier
    const unSeul = genreSing === 'f' ? 'une seule' : 'un seul';
    const deUn   = genreSing === 'f' ? "d'une"     : "d'un";
    const formulations = [
        `<p>${prenom} achète ${n} ${article} identiques pour ${total} € au total. Quel est le prix ${deUn} ${ctx.sing} ?</p>`,
        `<p>Pour ${n} ${article} identiques, ${prenom} paie ${total} €. Combien coûte ${unSeul} ${ctx.sing} ?</p>`,
        `<p>${n} ${article} identiques coûtent ${total} € en tout. Quel est le prix unitaire ?</p>`,
    ];
    return randomChoice(formulations);
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignaturePartCalcQcm617 = null;

const genPartCalcQcm = () => {
    let payload = construirePartCalcQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePartCalcQcm617; t++) {
        payload = construirePartCalcQcm();
    }
    derniereSignaturePartCalcQcm617 = payload.texte;
    return payload;
};

const construirePartCalcQcm = () => {
    const ctx = pickPartCalc();
    const { n, total, unit } = ctx;
    // Distracteurs familles (revue 2026-08-22) : multiplication (total × n),
    // virgule décalée (unit × 10), CHIFFRE ERRONÉ jumeau de gabarit (dernière
    // décimale modifiée) — remplace « prix total brut », facile à éliminer, et
    // supprime la fuite « bonne seule dans son gabarit » (27,9 % après strip).
    const distMul = mulDec(total, String(n));
    const distShift = shiftRight(unit, 1);
    const chiffreErrone = (() => {
        const canon = formatEur2(unit);
        if (!canon.includes(',')) return addDec(canon, '1');
        const [ip, dp] = canon.split(',');
        const last = parseInt(dp.slice(-1), 10);
        const delta = randomChoice([3, -3, 4, -4].filter((x) => last + x >= 1 && last + x <= 9));
        return `${ip},${dp.slice(0, -1)}${last + delta}`;
    })();

    const bonne = formatEur2(unit) + ' €';
    const candidats = [
        formatEur2(distMul) + ' €',
        distShift + ' €',
        chiffreErrone + ' €',
    ];
    const distracteurs = cleanDistracteurs(bonne, candidats, (i) => {
        if (i === 0) return formatEur2(shiftLeft(unit, 1)) + ' €';
        if (i === 1) return formatEur2(addDec(unit, '1')) + ' €';
        return formatEur2(subDec(unit, '1')) + ' €';
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnoncePartCalc(ctx),
        opts(L_PART_CALC_QCM),
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-28) : le coup de pouce
    // enseigne le passage en centimes (division entière posable en 6e).
    payload._meta.generatorKey = 'part_unite_calc';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignaturePartCalcSaisie617 = null;

const genPartCalcSaisie = () => {
    let payload = construirePartCalcSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePartCalcSaisie617; t++) {
        payload = construirePartCalcSaisie();
    }
    derniereSignaturePartCalcSaisie617 = payload.texte;
    return payload;
};

const construirePartCalcSaisie = () => {
    const ctx = pickPartCalc();
    const payload = makeSaisie(
        ctx.unit,
        buildEnoncePartCalc(ctx),
        { ...opts(L_PART_CALC_SAI), strict: false, suffixe: '€' },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-28) : le coup de pouce
    // enseigne le passage en centimes (division entière posable en 6e).
    payload._meta.generatorKey = 'part_unite_calc';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — nombre_parts (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const pickNbParts = () => {
    const item = randomChoice(POOL_NB_PARTS);
    const { nom: prenom, fem } = pickPrenom();
    return { ...item, prenom, fem };
};

const buildEnonceNbParts = (ctx) => {
    const { prenom, vol, unit_in, taille, unit_out, produit, contenant_plu } = ctx;
    const verbes = ['veut remplir', 'remplit', 'prépare'];
    const verbe = randomChoice(verbes);
    // Élision « de » → « d' » devant voyelle ou h muet
    const first = produit.normalize('NFD').replace(/[\u0300-\u036f]/g, '')[0].toLowerCase();
    const dePrep = /[aeiouh]/.test(first) ? "d'" : 'de ';
    return `<p class="italic text-slate-400">✏️ Pense à poser tes calculs sur ton cahier ou au brouillon avant de répondre.</p><p>${prenom} ${verbe} des ${contenant_plu} de ${taille} ${unit_out} avec ${vol} ${unit_in} ${dePrep}${produit}. Combien de ${contenant_plu} pleins peut-${ctx.fem ? 'elle' : 'il'} remplir ?</p>`;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignatureNbPartsQcm617 = null;

const genNbPartsQcm = () => {
    let payload = construireNbPartsQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureNbPartsQcm617; t++) {
        payload = construireNbPartsQcm();
    }
    derniereSignatureNbPartsQcm617 = payload.texte;
    return payload;
};

const construireNbPartsQcm = () => {
    const ctx = pickNbParts();
    const { vol, unit_in, unit_out, taille, nb, rawQuot } = ctx;
    // Convertir vol vers unit_out pour calcul direct (vol en unit_out, piège « inversion totale »).
    // si unit_in === 'L' et unit_out === 'cL' : ×100
    // si unit_in === 'kg' et unit_out === 'kg' : ×1
    const volOut = (unit_in === 'L' && unit_out === 'cL')
        ? mulDec(vol, '100')
        : vol;

    // Distracteurs fiche §6 exercice C :
    //  D1 = ceil : nb + 1 (l'élève arrondit par excès, oubliant que les parts incomplètes ne comptent pas)
    //  D2 = rawQuot : quotient fractionnaire brut (pré-stocké dans le pool, invariant rawQuot ≠ nb)
    //        → piège « je n'interprète pas la division, j'écris le résultat tel quel »
    //  D3 = taille : confusion part/nombre de parts (inversion du rôle dividende/diviseur)
    const distCeil = String(nb + 1);
    const distRaw = rawQuot;
    const distTaille = String(taille);
    const distVol = volOut;

    const bonne = String(nb);
    const candidats = [distCeil, distRaw, distTaille];
    const distracteurs = cleanDistracteurs(bonne, candidats, (i) => {
        if (i === 0) return String(nb - 1);
        if (i === 1) return distVol;
        return String(nb * 2);
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnonceNbParts(ctx),
        opts(L_NB_PARTS_QCM),
    );
    payload._meta.generatorKey = 'nombre_parts';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignatureNbPartsSaisie617 = null;

const genNbPartsSaisie = () => {
    let payload = construireNbPartsSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureNbPartsSaisie617; t++) {
        payload = construireNbPartsSaisie();
    }
    derniereSignatureNbPartsSaisie617 = payload.texte;
    return payload;
};

const construireNbPartsSaisie = () => {
    const ctx = pickNbParts();
    const payload = makeSaisie(
        String(ctx.nb),
        buildEnonceNbParts(ctx),
        { ...opts(L_NB_PARTS_SAI), strict: false, suffixe: ctx.contenant_plu },
    );
    payload._meta.generatorKey = 'nombre_parts';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — billet_rendu_partage (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const pickBilletCtx = () => {
    // Revue 2026-08-22 : tirage DYNAMIQUE via genChoisirData() (même moteur que
    // choisir_calculs / vrai_faux_resolution) — le pool figé de 10 items
    // plafonnait à 88 énoncés distincts (110 répétitions immédiates / 5000).
    // genChoisirData garantit division exacte en centimes et prix ≥ 0,50 €.
    const dyn = genChoisirData();
    if (dyn) {
        return { ...dyn, unit: dyn.bonne };
    }
    // Secours : pool figé historique
    const item = randomChoice(POOL_BILLET);
    const { nom: prenom, fem } = pickPrenom();
    // Vérification : (billet − rendu) / n == unit
    const paye = subDec(item.billet, item.rendu);
    const unitCheck = divDec(paye, item.n, 3);
    if (unitCheck !== item.unit) {
        return { n: 4, billet: '10', rendu: '5,20', unit: '1,20', paye: '4,80',
                 article: 'pains', sing: 'pain', prenom, fem };
    }
    return { ...item, paye, prenom, fem };
};

const buildEnonceBillet = (ctx) => {
    const { prenom, fem, n, billet, rendu, article, sing } = ctx;
    const pronom = fem ? 'Elle' : 'Il';
    return `<p class="italic text-slate-400">✏️ Pense à poser les opérations sur ton cahier ou au brouillon avant de répondre.</p><p>${prenom} achète ${n} ${article}. ${pronom} paie avec un billet de ${billet} € et on lui rend ${rendu} €. Quel est le prix d'un ${sing} ?</p>`;
};

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignatureBilletQcm617 = null;

const genBilletQcm = () => {
    let payload = construireBilletQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureBilletQcm617; t++) {
        payload = construireBilletQcm();
    }
    derniereSignatureBilletQcm617 = payload.texte;
    return payload;
};

const construireBilletQcm = () => {
    const ctx = pickBilletCtx();
    const { n, billet, rendu, unit, paye } = ctx;
    // Distracteurs fiche §6 exercice D (617) :
    //  A = bonne
    //  B = OUBLI SOUSTRACTION : billet / n
    //  C = DIFFÉRENCE NON DIVISÉE : paye
    //  D = RENDU DIVISÉ : rendu / n
    const distOubli = divDec(billet, n, 3);
    const distRendu = divDec(rendu, n, 3);

    const bonne = formatEur2(unit) + ' €';
    const candidats = [
        distOubli ? formatEur2(distOubli) + ' €' : billet + ' €',
        formatEur2(paye) + ' €',
        distRendu ? formatEur2(distRendu) + ' €' : rendu + ' €',
    ];
    const distracteurs = cleanDistracteurs(bonne, candidats, (i) => {
        if (i === 0) return formatEur2(addDec(unit, '0,10')) + ' €';
        if (i === 1) return formatEur2(subDec(unit, '0,05')) + ' €';
        return formatEur2(shiftRight(unit, 1)) + ' €';
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnonceBillet(ctx),
        opts(L_BILLET_QCM),
    );
    payload._meta.generatorKey = 'billet_rendu_partage';
    return payload;
};

const genBilletSaisie = () => {
    const ctx = pickBilletCtx();
    const payload = makeSaisie(
        ctx.unit,
        buildEnonceBillet(ctx),
        { ...opts(L_BILLET_SAI), strict: false, suffixe: '€' },
    );
    payload._meta.generatorKey = 'billet_rendu_partage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — forfait_variable (QCM + Saisie, calculatrice)
// ═══════════════════════════════════════════════════════════

const pickForfaitCtx = () => {
    const { nom: prenom, fem } = pickPrenom();
    // Tirage dynamique (revue 2026-08-22) : n multiple de 5 ∈ [10 ; 150],
    // forfait multiple de 5 ∈ [5 ; 40], tarif dans FORFAIT_TARIFS.
    const contexte = randomChoice(Object.keys(FORFAIT_CONTEXTES));
    const unit = randomChoice(FORFAIT_TARIFS);
    const n = String(randInt(2, 30) * 5);
    const forfait = String(randInt(1, 8) * 5);
    const total = addDec(forfait, mulDec(unit, n));
    return { forfait, unit, total, n, contexte, prenom, fem };
};

/**
 * Le calcul sort-il du programme de 6e ? (décision prof 2026-08-30)
 *
 * La division par un décimal n'est pas au programme : l'élève passe par les
 * CENTIMES, ce qui la ramène à une division par un entier. Reste le diviseur :
 * au-delà de DEUX chiffres (tarifs ≥ 1 €, soit 120 ou 150 centimes), la
 * division euclidienne sort du programme → la calculatrice est alors autorisée.
 * La taille du DIVIDENDE n'entre pas dans le test : le plafond habituel de
 * 4 chiffres est une convention de difficulté, pas une borne du programme.
 */
const centimes617 = (s) => Math.round(parseFloat(String(s).replace(',', '.')) * 100);
const forfaitHorsProgramme = (ctx) => String(centimes617(ctx.unit)).length > 2;

const MENTION_CALCULATRICE = '<p class="italic text-slate-400">🧮 Calculatrice autorisée pour cette question.</p>';

const buildEnonceForfait = (ctx) => {
    const template = FORFAIT_CONTEXTES[ctx.contexte];
    const phrase = template.phrase({ ...ctx, pronom: ctx.fem ? 'elle' : 'il' }, ctx.prenom);
    return forfaitHorsProgramme(ctx) ? phrase + MENTION_CALCULATRICE : phrase;
};

/** Arme la calculatrice UNIQUEMENT sur les tirages hors programme. */
const armerCalculatriceForfait = (payload, ctx) => {
    if (forfaitHorsProgramme(ctx)) {
        payload.contenu = { ...payload.contenu, calculatrice: true };
    }
    return payload;
};

const getForfaitUnit = (ctx) => FORFAIT_CONTEXTES[ctx.contexte].unit;

// Anti-répétition immédiate (revue 2026-08-22, recette standard).
let derniereSignatureForfaitQcm617 = null;

const genForfaitQcm = () => {
    let payload = construireForfaitQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureForfaitQcm617; t++) {
        payload = construireForfaitQcm();
    }
    derniereSignatureForfaitQcm617 = payload.texte;
    return payload;
};

const construireForfaitQcm = () => {
    const ctx = pickForfaitCtx();
    const { forfait, unit, total, n } = ctx;
    const unitLabel = getForfaitUnit(ctx);
    // Distracteurs fiche §6 exercice E (617) :
    //  A = bonne
    //  B = OUBLI DE SOUSTRACTION : total / unit
    //  C = FORFAIT DIVISÉ : forfait / unit
    //  D = VIRGULE DÉCALÉE : n / 10
    const distOubli = divDec(total, unit, 3);
    const distForfait = divDec(forfait, unit, 3);
    const distShift = shiftLeft(n, 1);

    const bonne = `${n} ${unitLabel}`;
    const candidats = [
        distOubli ? `${distOubli} ${unitLabel}` : `${parseInt(total, 10) * 2} ${unitLabel}`,
        distForfait ? `${distForfait} ${unitLabel}` : `${parseInt(forfait, 10)} ${unitLabel}`,
        `${distShift} ${unitLabel}`,
    ];
    const distracteurs = cleanDistracteurs(bonne, candidats, (i) => {
        if (i === 0) return `${parseInt(n, 10) + 10} ${unitLabel}`;
        if (i === 1) return `${parseInt(n, 10) - 10} ${unitLabel}`;
        return `${parseInt(n, 10) * 2} ${unitLabel}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(
        choix, bonne,
        buildEnonceForfait(ctx),
        opts(L_FORFAIT_QCM),
    );
    // Calculatrice CONDITIONNELLE (décision prof 2026-08-30) : par défaut non
    // (le coup de pouce enseigne le passage en centimes), sauf si le diviseur
    // en centimes dépasse 2 chiffres — le calcul sort alors du programme.
    armerCalculatriceForfait(payload, ctx);
    payload._meta.generatorKey = 'forfait_variable';
    return payload;
};

const genForfaitSaisie = () => {
    const ctx = pickForfaitCtx();
    const unitLabel = getForfaitUnit(ctx);
    const payload = makeSaisie(
        ctx.n,
        buildEnonceForfait(ctx),
        { ...opts(L_FORFAIT_SAI), strict: false, suffixe: unitLabel },
    );
    // Calculatrice CONDITIONNELLE (décision prof 2026-08-30) : par défaut non
    // (le coup de pouce enseigne le passage en centimes), sauf si le diviseur
    // en centimes dépasse 2 chiffres — le calcul sort alors du programme.
    armerCalculatriceForfait(payload, ctx);
    payload._meta.generatorKey = 'forfait_variable';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — choisir_calculs (QCM multiple uniquement)
// ═══════════════════════════════════════════════════════════

const genChoisirCalculsMulti = () => {
    // Génération dynamique : ~milliers de combinaisons (n × billet × rendu × prénom × article)
    const item = genChoisirData() || randomChoice(POOL_CHOISIR_CALCULS);
    const { prenom, fem, article, n, billet, rendu, bonne } = item;
    const pronom = fem ? 'Elle' : 'Il';

    // Vérification : (billet − rendu) / n == bonne
    const paye = subDec(billet, rendu);
    const check = divDec(paye, n, 3);
    if (check !== bonne) return buildChoisirFallback();

    // Revue 2026-08-22 : quota VARIABLE de vraies (1 ou 2, contre 2 constant),
    // et retrait de la forme distribuée « b ÷ n − r ÷ n » : la distributivité
    // est du cycle 4, un 6e ne peut pas la juger sans calculer les 2 membres.
    // Vraies (niveau 6e) : canonique et « soustraction déjà faite ».
    const vraies = shuffle([
        `(${billet} − ${rendu}) ÷ ${n}`,   // canonique ✓
        `${paye} ÷ ${n}`,                  // payé ÷ n ✓
    ]);
    // Filtre par VALEUR : si rendu = billet ÷ 2, « rendu ÷ n » vaut la bonne
    // réponse (9/5000 mesurés) — toute écriture de même valeur est écartée.
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    const bonneVal = toNum(bonne);
    const faussesPool = [
        { e: `(${billet} + ${rendu}) ÷ ${n}`, v: (toNum(billet) + toNum(rendu)) / n },   // ✗ addition
        { e: `${billet} ÷ ${n} − ${rendu}`, v: toNum(billet) / n - toNum(rendu) },       // ✗ ordre cassé
        { e: `${rendu} ÷ ${n}`, v: toNum(rendu) / n },                                   // ✗ mauvais nombre divisé
        { e: `${billet} − ${rendu} ÷ ${n}`, v: toNum(billet) - toNum(rendu) / n },       // ✗ parenthèses oubliées
    ];
    const fausses = shuffle(faussesPool
        .filter((f) => Math.abs(f.v - bonneVal) > 1e-9)
        .map((f) => f.e));
    const nbVraies = randInt(1, 2);
    const bonnes = vraies.slice(0, nbVraies);
    const choix = shuffle([...bonnes, ...fausses.slice(0, 4 - nbVraies)]);

    const texte = `<p>${prenom} achète ${n} ${article}. ${pronom} paie avec un billet de ${billet} € et on lui rend ${rendu} €. Parmi ces calculs, lesquels permettent de trouver le prix d'un ${item.article.replace(/s$/, '')} ? <em>(plusieurs réponses possibles)</em></p>`;

    const payload = makeQcm(
        choix,
        bonnes,
        texte,
        opts(L_CHOISIR),
        true, // multi
    );
    payload._meta.generatorKey = 'choisir_calculs';
    return payload;
};

const buildChoisirFallback = () => {
    // Revue 2026-08-22 : distributivité retirée ici aussi (hors programme 6e).
    const choix = ['(10 − 5,20) ÷ 4', '4,80 ÷ 4', '10 ÷ 4 − 5,20', '(10 + 5,20) ÷ 4'];
    const payload = makeQcm(
        choix,
        [choix[0], choix[1]],
        `<p>Diane achète 4 pains. Elle paie avec un billet de 10 € et on lui rend 5,20 €. Parmi ces calculs, lesquels permettent de trouver le prix d'un pain ? <em>(plusieurs réponses possibles)</em></p>`,
        opts(L_CHOISIR),
        true,
    );
    payload._meta.generatorKey = 'choisir_calculs';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — relier_enonces_calculs (Relier uniquement)
// ═══════════════════════════════════════════════════════════

const genRelierEnoncesCalculs = () => {
    // Génération dynamique : 4 énoncés (un par opération +/−/×/÷) sur même couple (a, b)
    const pool = genRelierData();
    const pairs = pool.items.map((it) => ({
        source: it.enonce,
        target: it.calcul,
    }));
    const payload = makeRelier(
        pairs,
        `<p>Associe chaque énoncé à son calcul.</p>`,
        opts(L_RELIER),
    );
    payload._meta.generatorKey = 'relier_enonces_calculs';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — vrai_faux_resolution (V/F uniquement)
// ═══════════════════════════════════════════════════════════

const genVraiFauxResolution = () => {
    // Réutilise la même structure que genChoisirData (problème billet−rendu÷n)
    const pb = genChoisirData() || randomChoice(POOL_VF_RESOLUTION);
    const { prenom, fem, n, article, sing, billet, rendu, bonne, paye } = pb;
    const pronom = fem ? 'Elle' : 'Il';

    // Vérification : (billet − rendu) / n == bonne
    if (divDec(subDec(billet, rendu), n, 3) !== bonne) {
        return buildVFResolutionFallback();
    }

    // Revue 2026-08-22 : quota VARIABLE (1 ou 2 vraies sur 6 lignes, contre
    // 3V/3F constant), distributivité retirée (cycle 4, hors programme 6e),
    // et filtre par VALEUR sur les fausses (rendu = billet/2 rendait
    // « rendu ÷ n » correcte alors qu'étiquetée fausse — 6/5000 mesurés).
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    const bonneVal = toNum(bonne);

    // Vraies (niveau 6e) : canonique + « soustraction déjà faite »
    const vraiesPool = shuffle([
        `(${billet} − ${rendu}) ÷ ${n} = ${formatEur2(bonne)} €`,
        `${paye} ÷ ${n} = ${formatEur2(bonne)} €`,
    ]);
    const nbVraies = randInt(1, 2);
    const vraies = vraiesPool.slice(0, nbVraies);

    // Fausses : familles d'erreur, gardées seulement si calculables (division
    // finie) et de valeur DIFFÉRENTE de la bonne réponse.
    const candidatsFaux = [];
    const billetDivN = divDec(billet, n, 3);
    if (billetDivN && Math.abs(toNum(billetDivN) - bonneVal) > 1e-9) {
        candidatsFaux.push(`${billet} ÷ ${n} = ${formatEur2(billetDivN)} €`); // oubli soustraction
    }
    const addRes = divDec(addDec(billet, rendu), n, 3);
    if (addRes && Math.abs(toNum(addRes) - bonneVal) > 1e-9) {
        candidatsFaux.push(`(${billet} + ${rendu}) ÷ ${n} = ${formatEur2(addRes)} €`); // addition
    }
    const renduDivN = divDec(rendu, n, 3);
    if (renduDivN && Math.abs(toNum(renduDivN) - bonneVal) > 1e-9) {
        candidatsFaux.push(`${rendu} ÷ ${n} = ${formatEur2(renduDivN)} €`); // mauvais nombre divisé
    }
    candidatsFaux.push(`${billet} − ${rendu} = ${formatEur2(paye)} €`); // étape incomplète (exact mais ne répond pas)
    candidatsFaux.push(`${paye} × ${n} = ${formatEur2(mulDec(paye, String(n)))} €`); // mult au lieu de div
    candidatsFaux.push(`${billet} + ${rendu} = ${formatEur2(addDec(billet, rendu))} €`); // addition sans division
    candidatsFaux.push(`${billet} × ${n} = ${formatEur2(mulDec(billet, String(n)))} €`); // mult du billet
    const fausses = shuffle(candidatsFaux).slice(0, 6 - nbVraies);

    const indexed = [
        ...vraies.map((l) => ({ l, br: 0 })),
        ...fausses.map((l) => ({ l, br: 1 })),
    ];
    const shuf = shuffle(indexed);

    const payload = makeVraiFaux(
        shuf.map((x) => x.l),
        shuf.map((x) => x.br),
        `<p class="italic text-slate-400">✏️ Pense à vérifier les calculs sur ton cahier ou au brouillon.</p><p>${prenom} achète ${n} ${article}. ${pronom} paie avec un billet de ${billet} € et on lui rend ${rendu} €. Un élève a proposé plusieurs résolutions pour trouver le prix d'un ${sing}. Pour chacune, dis si elle est correcte.</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_resolution';
    return payload;
};

const buildVFResolutionFallback = () => {
    // Revue 2026-08-22 : distributivité retirée ici aussi (hors programme 6e).
    const lignes = [
        '10 ÷ 4 = 2,5 €',
        '(10 − 5,20) ÷ 4 = 1,2 €',
        '(10 + 5,20) ÷ 4 = 3,8 €',
        '10 − 5,20 = 4,8 €',
        '5,20 ÷ 4 = 1,3 €',
        '4,80 ÷ 4 = 1,2 €',
    ];
    const bonnesReponses = [1, 0, 1, 1, 1, 0];
    const payload = makeVraiFaux(
        lignes, bonnesReponses,
        `<p class="italic text-slate-400">✏️ Pense à vérifier les calculs sur ton cahier ou au brouillon.</p><p>Diane achète 4 pains. Elle paie avec un billet de 10 € et on lui rend 5,20 €. Pour chaque résolution, dis si elle est correcte.</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_resolution';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 9 — drag_resolution (Drag uniquement)
// ═══════════════════════════════════════════════════════════

const genDragResolution = () => {
    // Génération dynamique : ~milliers de combinaisons
    const pb = genDragResData() || randomChoice(POOL_DRAG_RESOLUTION);
    const { prenom, n, gateau, jus, total_jus, depense, part } = pb;

    // Vérifications :
    // n × jus == total_jus ; gateau + total_jus == depense ; depense / n == part
    if (!isEquivalent(mulDec(jus, String(n)), total_jus)
        || !isEquivalent(addDec(gateau, total_jus), depense)
        || divDec(depense, n, 3) !== part) {
        return buildDragResFallback();
    }

    const phrase = `Prix des ${n} jus : ${n} × ${jus} = {{${total_jus}}} €\nDépense totale : ${gateau} + ${total_jus} = {{${depense}}} €\nDépense par enfant : ${depense} ÷ ${n} = {{${part}}} €`;

    // Distracteurs (1 par trou, familles fiche §6 exercice I) :
    //  P1 = virgule décalée ÷10 sur total_jus
    //  P2 = mauvaise opération : gateau − total_jus
    //  P3 = chiffre erroné sur part
    const p1 = shiftLeft(total_jus, 1);
    const p2 = subDec(gateau, total_jus);
    // p3 : dernière décimale modifiée
    const tweakLast = (q) => {
        if (!q.includes(',')) return `${parseInt(q, 10) - 1}`;
        const [ip, dp] = q.split(',');
        if (dp.length < 1) return `${ip},5`;
        const last = dp[dp.length - 1];
        const newLast = String((parseInt(last, 10) + 5) % 10);
        return `${ip},${dp.slice(0, -1)}${newLast}`;
    };
    const p3 = tweakLast(part);

    const bonnes = [total_jus, depense, part];
    const dist = [p1, p2, p3]
        .filter((x) => !bonnes.some((b) => isEquivalent(b, x)));
    // Déduplication (dist distincts entre eux)
    const distUniq = [];
    for (const d of dist) {
        if (!distUniq.some((x) => isEquivalent(x, d))) distUniq.push(d);
    }
    // Compléter si moins de 3
    let guard = 0;
    while (distUniq.length < 3 && guard < 20) {
        const candidats = [
            shiftLeft(depense, 1),
            shiftRight(part, 1),
            addDec(part, '1'),
            subDec(part, '0,5'),
        ];
        const cand = candidats[guard % candidats.length];
        if (!bonnes.some((b) => isEquivalent(b, cand))
            && !distUniq.some((x) => isEquivalent(x, cand))) {
            distUniq.push(cand);
        }
        guard++;
    }

    const payload = makeTexteTrou(
        phrase,
        `<p class="italic text-slate-400">✏️ Pense à poser les opérations sur ton cahier ou au brouillon avant de répondre.</p><p>${prenom} organise un goûter pour ${n} enfants. Elle achète un gâteau à ${gateau} € et ${n} jus de fruits à ${jus} € chacun. Combien dépense-t-elle par enfant au total ? Complète la résolution en faisant glisser les bons nombres.</p>`,
        { ...opts(L_DRAG), mode: 'drag', distracteurs: distUniq.slice(0, 3), strict: false },
    );
    payload._meta.generatorKey = 'drag_resolution';
    return payload;
};

const buildDragResFallback = () => {
    const payload = makeTexteTrou(
        `Prix des 8 jus : 8 × 1,20 = {{9,60}} €\nDépense totale : 12,40 + 9,60 = {{22}} €\nDépense par enfant : 22 ÷ 8 = {{2,75}} €`,
        `<p class="italic text-slate-400">✏️ Pense à poser les opérations sur ton cahier ou au brouillon avant de répondre.</p><p>Louise organise un goûter pour 8 enfants. Elle achète un gâteau à 12,40 € et 8 jus de fruits à 1,20 € chacun. Combien dépense-t-elle par enfant au total ? Complète la résolution en faisant glisser les bons nombres.</p>`,
        { ...opts(L_DRAG), mode: 'drag', distracteurs: ['0,96', '2,80', '2,60'], strict: false },
    );
    payload._meta.generatorKey = 'drag_resolution';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    part_unite_simple_qcm_unique:     genPartSimpleQcm,
    part_unite_simple_texte:          genPartSimpleSaisie,
    part_unite_calc_qcm_unique:       genPartCalcQcm,
    part_unite_calc_texte:            genPartCalcSaisie,
    nombre_parts_qcm_unique:          genNbPartsQcm,
    nombre_parts_texte:               genNbPartsSaisie,
    billet_rendu_partage_qcm_unique:  genBilletQcm,
    billet_rendu_partage_texte:       genBilletSaisie,
    forfait_variable_qcm_unique:      genForfaitQcm,
    forfait_variable_texte:           genForfaitSaisie,
    choisir_calculs_qcm_multiple:     genChoisirCalculsMulti,
    relier_enonces_calculs_relier:    genRelierEnoncesCalculs,
    vrai_faux_resolution_tableau_vf:  genVraiFauxResolution,
    drag_resolution_texte_trou_drag:  genDragResolution,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', relier: 'Relier', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'part_unite_simple',      label: L_PART_SIMPLE_QCM, formatsList: buildFormats('part_unite_simple') },
    { id: 'part_unite_calc',        label: L_PART_CALC_QCM,   formatsList: buildFormats('part_unite_calc') },
    { id: 'nombre_parts',           label: L_NB_PARTS_QCM,    formatsList: buildFormats('nombre_parts') },
    { id: 'billet_rendu_partage',   label: L_BILLET_QCM,      formatsList: buildFormats('billet_rendu_partage') },
    { id: 'forfait_variable',       label: L_FORFAIT_QCM,     formatsList: buildFormats('forfait_variable') },
    {
        // Fusion revue 2026-08-30 : « choisir les calculs corrects » (QCM multiple)
        // et « juger 6 résolutions » (V/F) posent la MÊME situation (achat, billet,
        // rendu) et font juger les MÊMES calculs candidats — deux formats d'une
        // seule tâche, deux cartes faisaient croire à deux exercices distincts.
        id: 'choisir_calculs',
        label: L_JUGER_CALCULS,
        formatsList: [...buildFormats('choisir_calculs'), ...buildFormats('vrai_faux_resolution')],
    },
    // Carte distincte : associer 4 énoncés DIFFÉRENTS à leurs calculs.
    { id: 'relier_enonces_calculs', label: L_RELIER,          formatsList: buildFormats('relier_enonces_calculs') },
    { id: 'drag_resolution',        label: L_DRAG,            formatsList: buildFormats('drag_resolution') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) =>
            k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
